import { api } from './client';
import { currentPagesSpace, type PagesSpace } from './pages';

export type B2bKind = 'offer' | 'report' | 'card' | 'guide';
export type B2bStatus = 'draft' | 'active' | 'archived';
export const B2B_OFFER_TYPES = ['Land-based', 'Affiliate service', 'Pozvánka', 'iGaming', 'Performance', 'Multibrand', 'AI asistent'] as const;
export type B2bOfferType = (typeof B2B_OFFER_TYPES)[number];

export interface B2bOfferTypeSet { types: readonly B2bOfferType[]; modules: readonly B2bOfferType[]; moduleParent: B2bOfferType | null }

const SPACE_OFFER_TYPES: Record<PagesSpace, B2bOfferTypeSet> = {
  affilmaster: { types: ['Land-based', 'Affiliate service', 'Pozvánka'], modules: [], moduleParent: null },
  affilplatform: { types: ['iGaming', 'Land-based'], modules: ['Performance', 'Multibrand', 'AI asistent'], moduleParent: 'iGaming' },
};

export function b2bOfferTypesFor(space: PagesSpace = currentPagesSpace()): B2bOfferTypeSet {
  return SPACE_OFFER_TYPES[space];
}

export interface B2bOutput {
  id: number;
  kind: B2bKind;
  slug: string;
  token: string;
  publicPath: string | null;
  title: string;
  offerTypes: B2bOfferType[];
  clientName: string | null;
  language: string;
  currency: string | null;
  partnerId: number | null;
  affiliateId: number | null;
  leadId: number | null;
  userId: number | null;
  status: B2bStatus;
  isDeal: boolean;
  validUntil: string | null;
  publishedAt: string | null;
  metaDescription: string | null;
  ogImagePath: string | null;
  photo: string | null;
  accessCode?: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  url: string;
  previewPath: string;
  publicLive?: boolean;
}

export interface B2bOutputDetail extends B2bOutput {
  html: string;
  dataJson: Record<string, unknown> | null;
  legacyPaths: string[];
}

export interface B2bCardData {
  jmeno: string;
  pozice: string;
  firma: string;
  email: string;
  telefon: string;
  telegram: string;
  web: string;
  foto?: string;
  key?: string;
}

export interface B2bView {
  viewedAt: string;
  ip: string | null;
  userAgent: string | null;
  referer: string | null;
}

export interface B2bPatch {
  title?: string;
  offerTypes?: B2bOfferType[];
  publishedAt?: string | null;
  clientName?: string | null;
  partnerId?: number | null;
  affiliateId?: number | null;
  leadId?: number | null;
  status?: B2bStatus;
  validUntil?: string | null;
  metaDescription?: string | null;
  html?: string;
  language?: string;
  defaultLanguage?: 'cs' | 'en' | 'es';
  accessCode?: string;
  card?: Partial<B2bCardData>;
}

export interface B2bGuideCreate {
  kind: 'guide';
  title: string;
  clientName?: string | null;
  partnerId?: number | null;
  leadId?: number | null;
  status?: 'draft' | 'active';
  validUntil?: string | null;
  metaDescription?: string | null;
  html: string;
  language?: string;
}

const base = () => `/api/v1/${currentPagesSpace()}/b2b`;

export const b2bApi = {
  list: (q: { kind?: B2bKind; status?: B2bStatus | 'deal'; type?: B2bOfferType; partnerId?: number; affiliateId?: number; leadId?: number; q?: string }) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') params.set(k, String(v));
    return api.get<B2bOutput[]>(`${base()}/outputs?${params.toString()}`);
  },
  get: (id: number) => api.get<B2bOutputDetail>(`${base()}/outputs/${id}`),
  create: (body: B2bGuideCreate) => api.post<B2bOutput>(`${base()}/outputs`, body),
  views: (id: number) => api.get<B2bView[]>(`${base()}/outputs/${id}/views`),
  update: (id: number, body: B2bPatch) => api.patch<B2bOutput>(`${base()}/outputs/${id}`, body),
  rotate: (id: number) => api.post<B2bOutput>(`${base()}/outputs/${id}/rotate`, {}),
  photo: (id: number, contentBase64: string) => api.post<B2bOutput>(`${base()}/outputs/${id}/photo`, { contentBase64 }),
  archive: (id: number) => api.del<void>(`${base()}/outputs/${id}`),
};

export function b2bOpenUrl(o: B2bOutput): string {
  const h = window.location.hostname;
  const local = h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
  return local || o.publicLive === false ? `${window.location.origin}${o.previewPath}` : o.url;
}

export type AmNewsAudience = 'inzerenti' | 'affiliates';
export type AmNewsStatus = 'draft' | 'published' | 'archived';

export interface AmNewsItem {
  id: number;
  token: string;
  audience: AmNewsAudience;
  title: string;
  excerpt: string | null;
  status: AmNewsStatus;
  publishedAt: string | null;
  expiresAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  url: string;
  previewPath: string;
  isPublic: boolean;
}

export interface AmNewsDetail extends AmNewsItem {
  bodyHtml: string;
}

export interface AmNewsPayload {
  title?: string;
  audience?: AmNewsAudience;
  excerpt?: string | null;
  bodyHtml?: string;
  status?: AmNewsStatus;
  publishedAt?: string | null;
  expiresAt?: string | null;
}

const news = () => `${base()}/news`;

export const amNewsApi = {
  list: (q: { audience?: AmNewsAudience; status?: AmNewsStatus; q?: string }) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') params.set(k, String(v));
    return api.get<AmNewsItem[]>(`${news()}?${params.toString()}`);
  },
  get: (id: number) => api.get<AmNewsDetail>(`${news()}/${id}`),
  create: (body: AmNewsPayload) => api.post<AmNewsDetail>(news(), body),
  update: (id: number, body: AmNewsPayload) => api.patch<AmNewsDetail>(`${news()}/${id}`, body),
  setStatus: (id: number, status: AmNewsStatus) => api.post<AmNewsDetail>(`${news()}/${id}/status`, { status }),
  rotate: (id: number) => api.post<AmNewsDetail>(`${news()}/${id}/rotate`, {}),
};

export function amNewsOpenUrl(o: AmNewsItem): string {
  const h = window.location.hostname;
  const local = h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
  return local ? `${window.location.origin}${o.previewPath}` : o.url;
}
